import React, { useEffect, useState, useRef } from 'react';
import { Mail, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  useGetEmailTemplateQuery,
  type EmailTemplateType,
} from '../../store/emailTemplateApi';
import type { EditableTemplateType } from '../email-templates/templateSections';
import { TEMPLATE_CONFIGS } from '../email-templates/templateSections';
import { assembleEmailHtml, replaceVariablesWithSamples } from '../email-templates/htmlAssembler';
import { parseSectionsFromHtml } from '../email-templates/sectionParser';

interface EmailPreviewCardProps {
  templateType: EditableTemplateType;
  sampleData?: Record<string, string>;
  renderedHtml?: string;
  renderedSubject?: string;
  isRendering?: boolean;
}

const EmailPreviewCard: React.FC<EmailPreviewCardProps> = ({
  templateType,
  sampleData,
  renderedHtml,
  renderedSubject,
  isRendering,
}) => {
  const { t } = useTranslation();
  const skipFetch = !!renderedHtml;
  const { data: template, isLoading } = useGetEmailTemplateQuery(
    templateType as EmailTemplateType,
    { skip: skipFetch }
  );
  const [iframeHeight, setIframeHeight] = useState(300);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const previewHtml = (() => {
    if (renderedHtml) return renderedHtml;

    if (!template?.body) return '';

    const sections = parseSectionsFromHtml(template.body, templateType);
    let html = assembleEmailHtml(templateType, sections);

    if (sampleData) {
      for (const [key, value] of Object.entries(sampleData)) {
        html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
      }
    }

    html = replaceVariablesWithSamples(html, templateType);

    return html;
  })();

  const previewSubject = (() => {
    if (renderedSubject) return renderedSubject;

    if (!template?.subject) return '';

    let subject = template.subject;

    if (sampleData) {
      for (const [key, value] of Object.entries(sampleData)) {
        subject = subject.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
      }
    }

    const config = TEMPLATE_CONFIGS[templateType];
    for (const v of config.variables) {
      subject = subject.replace(new RegExp(`\\{\\{${v.name}\\}\\}`, 'g'), v.sampleValue);
    }

    return subject;
  })();

  // Auto-resize iframe
  useEffect(() => {
    if (!iframeRef.current || !previewHtml) return;

    const handleLoad = () => {
      try {
        const doc = iframeRef.current?.contentDocument;
        if (doc?.body) {
          setIframeHeight(Math.min(doc.body.scrollHeight + 20, 500));
        }
      } catch {
        // Cross-origin issues - keep default height
      }
    };

    const iframe = iframeRef.current;
    iframe.addEventListener('load', handleLoad);
    return () => iframe.removeEventListener('load', handleLoad);
  }, [previewHtml]);

  if (isLoading || isRendering) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-semibold text-gray-700">{t('shared.emailPreview.heading')}</span>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader className="w-5 h-5 animate-spin text-purple-600" />
          <span className="ml-2 text-sm text-gray-500">{t('shared.emailPreview.loading')}</span>
        </div>
      </div>
    );
  }

  if (!previewHtml) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2 mb-1.5">
          <Mail className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-semibold text-gray-700">{t('shared.emailPreview.heading')}</span>
        </div>
        {previewSubject && (
          <div className="text-xs text-gray-500">
            <span className="font-medium">{t('shared.emailPreview.subjectLabel')}</span> {previewSubject}
          </div>
        )}
      </div>
      <iframe
        ref={iframeRef}
        srcDoc={previewHtml}
        title={t('shared.emailPreview.heading')}
        className="w-full"
        style={{ height: iframeHeight, border: 'none' }}
        sandbox="allow-same-origin"
      />
    </div>
  );
};

export default EmailPreviewCard;
